import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Table, Button, Badge, Spinner, Alert, InputGroup, Form } from 'react-bootstrap'
import client from '../../api/client'
import PromoForm from './PromoForm'

function PromoCodes() {
  const [promoCodes, setPromoCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingPromo, setEditingPromo] = useState(null)
  const [formLoading, setFormLoading] = useState(false)
  const [copiedId, setCopiedId] = useState(null)

  useEffect(() => {
    fetchPromoCodes()
  }, [])

  const fetchPromoCodes = async () => {
    setLoading(true)
    try {
      const response = await client.get('/admin/api/v1/promo-codes')
      setPromoCodes(response.data.promo_codes || response.data || [])
    } catch (err) {
      console.error('Error fetching promo codes:', err)
      setError('Erreur lors du chargement des codes promo')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingPromo(null)
    setShowForm(true)
  }

  const handleEdit = (promo) => {
    setEditingPromo(promo)
    setShowForm(true)
  }

  const handleDelete = async (promo) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le code "${promo.code}" ?`)) {
      return
    }

    try {
      await client.delete(`/admin/api/v1/promo-codes/${promo.id}`)
      setSuccess('Code promo supprimé avec succès')
      fetchPromoCodes()
    } catch (err) {
      console.error('Error deleting promo code:', err)
      setError('Erreur lors de la suppression du code promo')
    }
  }

  const handleFormSubmit = async (formData) => {
    setFormLoading(true)
    try {
      if (editingPromo) {
        await client.patch(`/admin/api/v1/promo-codes/${editingPromo.id}`, formData)
        setSuccess('Code promo mis à jour avec succès')
      } else {
        await client.post('/admin/api/v1/promo-codes', formData)
        setSuccess('Code promo créé avec succès')
      }
      setShowForm(false)
      fetchPromoCodes()
    } catch (err) {
      console.error('Error saving promo code:', err)
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement du code promo')
    } finally {
      setFormLoading(false)
    }
  }

  const copyMagicLink = (promo) => {
    const link = promo.invitation_link || `https://picchu.ai/signup?promo=${promo.code}`
    navigator.clipboard.writeText(link)
    setCopiedId(promo.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const getStatusBadge = (promo) => {
    if (!promo.active) return <Badge bg="secondary">Inactif</Badge>
    if (promo.max_uses > 0 && promo.uses >= promo.max_uses) return <Badge bg="warning">Épuisé</Badge>
    if (promo.expires_at && new Date(promo.expires_at) < new Date()) return <Badge bg="danger">Expiré</Badge>
    return <Badge bg="success">Actif</Badge>
  }

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <h1 className="h3">Codes promo</h1>
          <p className="text-muted">
            Gérez les codes promo et générez des liens magiques d'invitation
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleCreate}>
            + Nouveau code promo
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : promoCodes.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <p>Aucun code promo</p>
              <Button variant="primary" onClick={handleCreate}>
                Créer un code promo
              </Button>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Réduction</th>
                  <th>Utilisations</th>
                  <th>Statut</th>
                  <th>Lien magique</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {promoCodes.map((promo) => (
                  <tr key={promo.id}>
                    <td>
                      <strong>{promo.code}</strong>
                      {promo.description && (
                        <div className="small text-muted">{promo.description}</div>
                      )}
                    </td>
                    <td>
                      {promo.discount_type === 'percentage' && `${promo.discount_value}%`}
                      {promo.discount_type === 'fixed' && `${promo.discount_value}€`}
                      {promo.discount_type === 'trial_days' && `${promo.discount_value} jours`}
                    </td>
                    <td>
                      {promo.uses || 0}
                      {promo.max_uses > 0 && ` / ${promo.max_uses}`}
                      {promo.max_uses === 0 && ' / ∞'}
                    </td>
                    <td>{getStatusBadge(promo)}</td>
                    <td>
                      <InputGroup size="sm" style={{ maxWidth: '300px' }}>
                        <Form.Control
                          type="text"
                          readOnly
                          value={promo.invitation_link || `https://picchu.ai/signup?promo=${promo.code}`}
                          className="bg-light"
                        />
                        <Button
                          variant={copiedId === promo.id ? 'success' : 'outline-secondary'}
                          onClick={() => copyMagicLink(promo)}
                        >
                          {copiedId === promo.id ? '✓' : 'Copier'}
                        </Button>
                      </InputGroup>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(promo)}
                      >
                        Modifier
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(promo)}
                      >
                        Supprimer
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <PromoForm
        show={showForm}
        onHide={() => setShowForm(false)}
        onSubmit={handleFormSubmit}
        promo={editingPromo}
        loading={formLoading}
      />
    </Container>
  )
}

export default PromoCodes
